import { defaultDataTransformer } from '@aspectus/resource-commons';
import { createResource, createReceiver, createSender } from '@md/resources';
import { prefixAPI } from '@md/api';

const r = (tpl, f = createReceiver) => f(prefixAPI(tpl)).transform(defaultDataTransformer);

export const freightOrders = {
  list: r('/freight-orders/list/{?s,filters,order_by,limit,offset}'),
  exportList: createResource(prefixAPI('/freight-orders/list/xls/export/{?filters,order_by}')),
  simpleList: r('/freight-orders/simple/list/{?s,filters,order_by,limit,offset}'),
  aggregate: r('/freight-orders/aggregate/{?filters,order_by,limit,offset}'),
  addons: {
    statuses: r('/freight-orders/addons/statuses/'),
  },
  filters: {
    list: r('/freight-orders/filters/list/'),
    available: r('/freight-orders/filters/list/{?filters}'),
  },
  create: r('/freight-orders/create/', createSender),
  retrieve: r('/freight-orders{/id}/retrieve/'),
  update: r('/freight-orders{/id}/update/', createSender).config('method', 'PATCH'),
  pdf: r('/freight-orders{/id}/pdf/'),

  historyList: r('/freight-orders/history/list/{?s,filters,order_by,limit,offset}'),
  historyFilters: {
    schema: r('/freight-orders/filters/list/?schema_only=1'),
    list: r('/freight-orders/history/filters/list/'),
    available: r('/freight-orders/history/filters/list/{?filters}'),
  },
};

export const managerServiceExecutionsApi = {
  awaiting: r(
    '/manager/service-executions/awaiting/{?limit,offset}'
  ),
  confirm: r(
    '/manager/service-executions/confirm/',
    createSender
  ),
};

export const parcels = {
  list: r('/parcels/list/{?filters,order_by,limit,offset}'),
  exportList: createResource(prefixAPI('/parcels/list/xls/export/{?filters,order_by}')),
  aggregate: r('/parcels/aggregate/{?filters,order_by,limit,offset}'),
  addons: {
    statuses: r('/parcels/addons/statuses/'),
  },
  receivedCargo: {
    list: r('/parcels/received-cargo/list/{?received_cargo,limit,offset}'),
    create: r('/parcels/received-cargo/create/', createSender),
    retrieve: r(
      '/parcels/received-cargo/parcel{/id}/retrieve/'
    ),
    updateAttachments: r(
      '/parcels/received-cargo/parcel{/id}/attachments/update/',
      createSender
    ).config('method', 'PATCH'),
    updateIntake: r(
      '/parcels/received-cargo/parcel{/id}/intake/update/',
      createSender
    ).config('method', 'PATCH'),
    // Ручной CRM-аналог operator Stage 2 process.
    process: r(
      '/parcels/received-cargo/parcel{/id}/process/',
      createSender
    ),
    // Browser CRM работает через session adapters,
    // а не через Token-only /operator/*.
    split: r(
      '/parcels/received-cargo/parcel{/id}/split/',
      createSender
    ),
    splitCancel: r(
      '/parcels/received-cargo/parcel{/id}/split-cancel/',
      createSender
    ),
    services: r(
      '/parcels/received-cargo/services/list/{?limit,offset}'
    ),
    operatorServices: r(
      '/parcels/received-cargo/operator-services/list/{?limit,offset}'
    ),
    printLabel: r(
      '/parcels/received-cargo/parcel{/id}/print-label/',
      createSender
    ),
  },
  mix: {
    current: r(
      '/parcels/mix/current/'
    ),
    add: r(
      '/parcels/mix/add/',
      createSender
    ),
    remove: r(
      '/parcels/mix/remove/',
      createSender
    ),
    complete: r(
      '/parcels/mix/complete/',
      createSender
    ),
  },
  // CRM/session contour выполнения услуг оператором склада.
  // Не используем Token-only /operator/* из browser frontend.
  operatorServiceExecutions: {
    list: r(
      '/parcels/service-executions/list/{?filters,order_by,limit,offset}'
    ),

    filters: {
      list: r(
        '/parcels/service-executions/filters/list/'
      ),

      available: r(
        '/parcels/service-executions/filters/list/{?filters}'
      ),
    },
    postfactum: r(
      '/parcels/service-executions/postfactum/{?limit,offset}'
    ),
    needsAttention: r(
      '/parcels/service-executions/needs-attention/{?limit,offset}'
    ),
    start: r(
      '/parcels/service-executions{/id}/start/',
      createSender
    ),
    execute: r(
      '/parcels/service-executions{/id}/execute/',
      createSender
    ),
    // «Не нужно»: услуга закрывается без выполнения, причина обязательна
    notNeeded: r(
      '/parcels/service-executions{/id}/not-needed/',
      createSender
    ),
  },
  // Client-cabinet WMS service actions.
  // Не расширяем generic Parcel update этими операциями.
  cabinet: {
    retrieve: r(
      '/cabinet/parcels{/id}/retrieve/'
    ),
    orderService: r(
      '/cabinet/parcels{/id}/order-service/',
      createSender
    ),
    myServices: r(
      '/cabinet/my-services/'
    ),
    serviceExecutions: {
      confirm: r(
        '/cabinet/service-executions/confirm/',
        createSender
      ),
      comment: r(
        '/cabinet/service-executions{/id}/comment/',
        createSender
      ),
    },
  },
  plural: {
    changeClientRate: r('/parcels/plural/change/client-rate/', createSender),
    changeStatus: r('/parcels/plural/change/status/', createSender),
    changeIsShipped: r('/parcels/plural/change/is-shipped/', createSender),
    changeTransferMethod: r('/parcels/plural/change/transfer-method/', createSender),
    changeShipment: r('/parcels/plural/change/shipment/', createSender),
  },
  filters: {
    schema: r('/parcels/filters/list/?schema_only=1'),
    list: r('/parcels/filters/list/'),
    available: r('/parcels/filters/list/{?filters}'),
  },
  create: r('/parcels/create/', createSender),
  delete: r('/parcels/{id}/delete/', createSender).config('method', 'POST'),
  dispatch: r('/parcels/dispatch/', createSender),
  retrieve: r('/parcels{/id}/retrieve/'),
  cancel: r('/parcels{/id}/cancel/', createSender),
  cancelReasons: r('/parcels/cancel-reasons/'),
  cancellations: r('/parcels/cancellations/list/{?filters,order_by,limit,offset}'),
  cancellationsFilters: {
    list: r('/parcels/cancellations/filters/list/'),
    available: r('/parcels/cancellations/filters/list/{?filters}'),
  },
  cancellationRestorePlan: r('/parcels/cancellations{/id}/restore-plan/'),
  cancellationRestore: r('/parcels/cancellations{/id}/restore/', createSender),
  shortages: r('/parcels/shortages/list/{?filters,order_by,limit,offset}'),
  // вкладки журнала: основной список — только текущие недоезды
  shortagesTabs: {
    open: r('/parcels/shortages/list/open/{?filters,order_by,limit,offset}'),
    found: r('/parcels/shortages/list/found/{?filters,order_by,limit,offset}'),
    deleted: r('/parcels/shortages/list/deleted/{?filters,order_by,limit,offset}'),
    all: r('/parcels/shortages/list/{?filters,order_by,limit,offset}'),
  },
  shortagesCounts: r('/parcels/shortages/counts/'),
  shortagesNote: r('/parcels/shortages{/id}/note/', createSender),
  chargeBalance: r('/parcels{/id}/charge-balance/', createSender),
  shortagesFilters: {
    list: r('/parcels/shortages/filters/list/'),
    available: r('/parcels/shortages/filters/list/{?filters}'),
  },
  shortagesPreview: r('/parcels/shortages/preview/', createSender),
  shortagesOpen: r('/parcels/shortages/open/', createSender),
  shortagesDeleteReasons: r('/parcels/shortages/delete-reasons/'),
  shortagesFound: r('/parcels/shortages{/id}/found/', createSender),
  shortagesTransactions: r('/parcels/shortages{/id}/transactions/'),
  shortagesDelete: r('/parcels/shortages{/id}/delete/', createSender),
  money: r('/parcels{/id}/money/'),
  toUnidentifiedUa: r('/parcels{/id}/to-unidentified-ua/', createSender),
  update: r('/parcels{/id}/update/', createSender).config('method', 'PATCH'),
  receive: r('/parcels/receive/', createSender),
  createChildMixed: r('/parcels/create-child-mixed/', createSender),
  updateChildMixed: r('/parcels{/id}/update-child-mixed/', createSender).config('method', 'PATCH'),
  retrieveParentMixed: r('/parcels{/id}/retrieve-parent-mixed/'),
  updateParentMixed: r('/parcels{/id}/update-parent-mixed/', createSender).config('method', 'PATCH'),
  attachmentsArchive: createResource(
    prefixAPI('/parcels{/id}/attachments/archive/')
  ),
  // Найденное LOST-место возвращается в логистический flow
  // только через специальный admin action.
  reactivateLost: r(
    '/admin/parcels{/id}/reactivate-lost/',
    createSender
  ),

  historyList: r('/parcels/history/list/{?s,filters,order_by,limit,offset}'),
  historyFilters: {
    list: r('/parcels/history/filters/list/'),
    available: r('/parcels/history/filters/list/{?filters}'),
  },
};

export const shipments = {
  list: r('/shipments/list/{?filters,order_by,limit,offset}'),
  simpleList: r('/shipments/simple/list/{?filters,order_by,limit,offset}'),
  aggregate: r('/shipments/aggregate/{?filters,order_by,limit,offset}'),
  filters: {
    list: r('/shipments/filters/list/'),
    available: r('/shipments/filters/list/{?filters}'),
  },
  getNumber: r('/shipments/get-number/', createSender),
  getNumberOfSubparcel: r('/shipments/get-number-of-subparcel/', createSender),
  regenerate: r('/shipments/regenerate/', createSender),
  delete: r('/shipments/{id}/delete/', createSender).config('method', 'POST'),
  create: r('/shipments/create/', createSender),
  retrieve: r('/shipments{/id}/retrieve/'),
  tracking: r('/shipments{/id}/tracking/'),
  excel: r('/shipments{/id}/excel/'),
  pdf: r('/shipments{/id}/pdf/'),
  attachmentsArchive: createResource(prefixAPI('/shipments{/id}/attachments/archive/')),
  update: r('/shipments{/id}/update/', createSender).config('method', 'PATCH'),
  historyList: r('/shipments/history/list/{?s,filters,order_by,limit,offset}'),
  historyFilters: {
    list: r('/shipments/history/filters/list/'),
    available: r('/shipments/history/filters/list/{?filters}'),
  },
};

// export const shipmentParcels = {
//   list: r('/shipment-parcels/list/{?filters,order_by,limit,offset}'),
//   create: r('/shipment-parcels/create/', createSender),
//   listCreate: r('/shipment-parcels/list-create/', createSender),
//   changeShipment: r('/shipment-parcels/change-shipment/', createSender),
//   departureCheck: r('/shipment-parcels/departure-check/', createSender),
//   destinationCheck: r('/shipment-parcels/destination-check/', createSender),
// };

export const locationsApi = {
  list: r('/locations/departure/list/'),
};

export const unidentifiedApi = {
  list: r('/unidentified/list/{?filters,order_by,limit,offset}'),
  listUa: r('/unidentified/ua/list/{?filters,order_by,limit,offset}'),
  filtersUa: {
    list: r('/unidentified/ua/filters/list/'),
    available: r('/unidentified/ua/filters/list/{?filters}'),
  },
  filters: {
    list: r('/unidentified/filters/list/'),
    available: r('/unidentified/filters/list/{?filters}'),
  },
  create: r('/unidentified/create/', createSender),
  createUa: r('/unidentified/ua/create/', createSender),
  retrieve: r('/unidentified{/id}/retrieve/'),
  update: r('/unidentified{/id}/update/', createSender).config('method', 'PATCH'),
  completeDefaults: r('/unidentified{/id}/complete/'),
  complete: r('/unidentified{/id}/complete/', createSender),
  completePreview: r('/unidentified{/id}/complete/preview/', createSender),
  uaTabs: {
    none: r('/unidentified/ua/list/none/{?filters,order_by,limit,offset}'),
    client: r('/unidentified/ua/list/client/{?filters,order_by,limit,offset}'),
  },
  uaCounts: r('/unidentified/ua/counts/'),
  historyList: r('/unidentified/history/list/{?s,filters,order_by,limit,offset}'),
  historyFilters: {
    list: r('/unidentified/history/filters/list/'),
    available: r('/unidentified/history/filters/list/{?filters}'),
  },
};
export const unidentifiedSearchApi = {
  list: r('/unidentified-search/list/{?filters,order_by,limit,offset}'),
  filters: {
    list: r('/unidentified-search/filters/list/'),
    available: r('/unidentified-search/filters/list/{?filters}'),
  },
  create: r('/unidentified-search/create/', createSender),
  retrieve: r('/unidentified-search{/id}/retrieve/'),
  perform: r('/unidentified-search{/id}/perform/', createSender),
  confirm: r('/unidentified-search{/id}/confirm/', createSender),
  reject: r('/unidentified-search{/id}/reject/', createSender),
  reassign: r('/unidentified-search{/id}/reassign/', createSender),
  close: r('/unidentified-search{/id}/close/', createSender),
  reopen: r('/unidentified-search{/id}/reopen/', createSender),
  groupsCounts: r('/unidentified-search/groups/counts/'),
  update: r('/unidentified-search{/id}/update/', createSender).config('method', 'PATCH'),
  multiplePerform: r('/unidentified-search/multiple/perform/', createSender),
  historyList: r('/unidentified-search/history/list/{?s,filters,order_by,limit,offset}'),
  historyFilters: {
    list: r('/unidentified-search/history/filters/list/'),
    available: r('/unidentified-search/history/filters/list/{?filters}'),
  },
};

export const receivedCargoesApi = {
  list: r('/received-cargoes/list/{?filters,order_by,limit,offset}'),
  filters: {
    list: r('/received-cargoes/filters/list/'),
    available: r('/received-cargoes/filters/list/{?filters}'),
  },
  create: r('/received-cargoes/create/', createSender),
  retrieve: r('/received-cargoes{/id}/retrieve/'),
  update: r('/received-cargoes{/id}/update/', createSender).config('method', 'PATCH'),
  close: r('/received-cargoes{/id}/close/', createSender),
  reopen: r('/received-cargoes{/id}/reopen/', createSender),
  // Stage 1 batch generation of 100x100 acceptance labels.
  printAcceptanceLabels: r(
    '/received-cargoes{/id}/print-acceptance-labels/', createSender),
  // Взять ReceivedCargo в ручную Stage 2 обработку из CRM.
  takeForProcessing: r(
    '/received-cargoes{/id}/take-for-processing/',
    createSender
  ),
  pauseProcessing: r(
    '/received-cargoes{/id}/pause-processing/',
    createSender
  ),
  completeProcessing: r(
    '/received-cargoes{/id}/complete-processing/',
    createSender
  ),
  // Admin Stage 2 completion actions.
  forceComplete: r(
    '/admin/received-cargoes{/id}/force-complete/',
    createSender
  ),
  // админ снимает чужую блокировку RC
  releaseLock: r(
    '/admin/received-cargoes{/id}/release-lock/',
    createSender
  ),
  returnToProcessing: r(
    '/admin/received-cargoes{/id}/return-to-processing/',
    createSender
  ),
  historyList: r('/received-cargoes/history/list/{?s,filters,order_by,limit,offset}'),
  historyFilters: {
    list: r('/received-cargoes/history/filters/list/'),
    available: r('/received-cargoes/history/filters/list/{?filters}'),
  },
};

export const categoriesApi = {
  create: r('/categories/create/', createSender),
  list: r('/categories/list/{?filters,order_by,limit,offset}'),
  update: r('/categories{/id}/update/', createSender).config('method', 'PATCH'),
  retrieve: r('/categories{/id}/retrieve/'),
}
export const clientServicesApi = {
  create: r('/client-services/create/', createSender),
  list: r('/client-services/list/'),
  catalog: r('/client-services/catalog/'),
  retrieve: r('/client-services{/id}/retrieve/'),
  update: r(
    '/client-services{/id}/update/',
    createSender
  ).config('method', 'PATCH'),
};

export const equipmentDevicesApi = {
  list: r('/equipment/devices/list/'),
};